import React, { useState } from 'react';
import { X, Search, User, Shield, AlertCircle } from 'lucide-react';
import { useUsers } from '../contexts/UsersContext';

interface UserPromotionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (message: string) => void;
}

const UserPromotionModal: React.FC<UserPromotionModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [email, setEmail] = useState('');
  const [searching, setSearching] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [foundUser, setFoundUser] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  
  const { getUserByEmail, promoteToAdmin } = useUsers();

  const handleSearch = async () => {
    if (!email.trim()) {
      setError('Digite um email válido');
      return;
    }

    setSearching(true);
    setError(null);
    setFoundUser(null);

    try {
      const user = await getUserByEmail(email.trim());
      
      if (!user) {
        setError('Usuário não encontrado. Verifique se o email está correto.');
        return;
      }

      if (user.role === 'admin') {
        setError('Este usuário já é administrador.');
        return;
      }

      if (!user.is_active) {
        setError('Este usuário está suspenso e não pode ser promovido.');
        return;
      }

      setFoundUser(user);
    } catch (error) {
      setError('Erro ao buscar usuário. Tente novamente.');
    } finally {
      setSearching(false);
    }
  };

  const handlePromote = async () => {
    if (!foundUser) return;

    setPromoting(true);
    setError(null);

    try {
      const result = await promoteToAdmin(foundUser.email);
      
      if (result.success) {
        onSuccess(result.message);
        handleClose();
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('Erro inesperado ao promover usuário.');
    } finally {
      setPromoting(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setFoundUser(null);
    setError(null);
    setSearching(false);
    setPromoting(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-[#1a1d21] rounded-3xl p-8 max-w-md w-full mx-4 border border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-[#0FFF55] rounded-full flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#121316]" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">Promover a Administrador</h3>
              <p className="text-gray-400 text-sm">Adicionar novo administrador</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Search Section */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Email do usuário
            </label>
            <div className="flex space-x-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Digite o email do usuário"
                className="flex-1 bg-[#121316] border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:border-[#0FFF55] focus:outline-none"
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <button
                onClick={handleSearch}
                disabled={searching || !email.trim()}
                className="bg-[#0FFF55] text-[#121316] px-4 py-3 rounded-xl hover:bg-[#0dd149] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {searching ? (
                  <div className="w-5 h-5 border-2 border-[#121316] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Search size={20} />
                )}
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-center space-x-2 p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
              <AlertCircle className="w-5 h-5 text-red-400" />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Found User */}
          {foundUser && (
            <div className="bg-[#121316] rounded-xl p-4 border border-gray-600">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-[#0FFF55] rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-[#121316]" />
                </div>
                <div className="flex-1">
                  <h4 className="text-white font-semibold">
                    {foundUser.full_name || 'Usuário sem nome'}
                  </h4>
                  <p className="text-gray-400 text-sm">{foundUser.email}</p>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className="text-xs text-gray-500">
                      Membro desde {new Date(foundUser.created_at).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Shield className="w-4 h-4 text-blue-400" />
                  <p className="text-blue-400 text-sm">
                    Este usuário será promovido a administrador e terá acesso completo ao painel administrativo.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4">
            <button
              onClick={handleClose}
              className="flex-1 bg-gray-600 text-white px-4 py-3 rounded-xl hover:bg-gray-700 transition-colors"
            >
              Cancelar
            </button>
            {foundUser && (
              <button
                onClick={handlePromote}
                disabled={promoting}
                className="flex-1 bg-[#0FFF55] text-[#121316] px-4 py-3 rounded-xl hover:bg-[#0dd149] transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
              >
                {promoting ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-4 h-4 border-2 border-[#121316] border-t-transparent rounded-full animate-spin" />
                    <span>Promovendo...</span>
                  </div>
                ) : (
                  'Promover a Admin'
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserPromotionModal; 